import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import classes from './ResourceCard.module.scss';
import panelClasses from './ResourceCardPanels/ResourceCardPanel.module.scss';
import Button from '@/components/Button/Button';
import ActionButtons from './ActionButtons';
import SuggestEditPanel from '@/components/Resources/ResourceCard/ResourceCardPanels/SuggestEditPanel';
import ResourceCardBanner from '@/components/Resources/ResourceCardBanner/ResourceCardBanner';
import t from '@/utils/Language/ResourceCard-translations';
import OrganizationPropType from '@/types/organizations';
import { InfoPanel, MapPanel, ContactPanel, ButtonPanel, OrgNameAndTitlePanel, AddressPanel, HasEligibilityRequirements } from './ResourceCardPanels';
import VirtualAvailableBadge from './ResourceCardPanels/VirtualAvailableBadge';
import { ServiceUtilization } from './ResourceCardPanels/ServiceUtilization';
import StarRating from '@/components/Resources/StarRating/StarRating';
import CLRIcon from '@/components/CLRIcon/CLRIcon';
import { AccountContext } from '@/context/account.context';
import ReferralNetwork from '@/utils/referral_network';
import { resourceHelpers } from '@/utils/helpers';

/**
 * General-purpose resource card used across the main app.
 * Used by: FetchableList (Collections)
 * (and is also intended to be used for future pages on prod: Search ResultList, BookmarkList)
 */
const ResourceCard = (
  {
    resource,
    actionButtons,
    language,
    includeFilterNote,
    totalUtilizations,
    showSuggestEdits = true,
    showResourceRemoveFromCollectionLink = false,
    removeItemFromCollection
  },
  ...props
) => {
  const account = useContext(AccountContext);
  const [openPanel, setOpenPanel] = useState();
  const [orgNetwork, setOrgNetwork] = useState(false);
  const [resourceNetworkIds, setResourceNetworkIds] = useState([]);

  useEffect(() => {
    if (account.data?.affiliation?.organizationReferralNetworks && resource) {
      resourceHelpers.checkResourceNetworkStatus(resource).then((matchingNetworks) => {
        if (matchingNetworks) {
          setOrgNetwork(ReferralNetwork.inspectAffiliation(account.data));
          setResourceNetworkIds(matchingNetworks.map((n) => n.id));
        }
      });
    }
  }, [account.data, resource]);

  const suggestEditPanelClassName = {
    tagIcon: classes.tagIcon,
    editSuggestContainer: classes.editSuggestContainer,
    editSuggestLink: classes.editSuggestLink
  };

  const isOrganization = resource.resource_type === 'Organization';

  const panelButtons = [
    {
      label: 'Contact',
      panel: 'contact',
      icon: { style: 'fas', font: 'phone' },
      type: 'default',
      shape: 'rounded',
      text: t('Contact'),
      groupPosition: 'left',
      styles: { width: 'full' },
      containerClass: isOrganization ? panelClasses.orgButtonGap : '',
      onClick: () => setOpenPanel(openPanel === 'contact' ? null : 'contact')
    },
    ...(!isOrganization
      ? [
          {
            label: 'Info',
            panel: 'info',
            icon: { style: 'fas', font: 'check-circle' },
            type: 'default',
            shape: 'square',
            text: t('Details'),
            styles: { width: 'full' },
            onClick: () => setOpenPanel(openPanel === 'info' ? null : 'info')
          }
        ]
      : []),
    {
      label: 'Map',
      panel: 'map',
      icon: { style: 'fas', font: 'map-marker' },
      type: 'default',
      shape: 'rounded',
      groupPosition: 'right',
      text: t('Map'),
      styles: { width: 'full' },
      onClick: () => setOpenPanel(openPanel === 'map' ? null : 'map')
    }
  ];

  const closePanel = () => {
    setOpenPanel(null);
  };

  const orgNameAndTitlePanelClassNames = {
    resourceIcon: classes.resourceIcon,
    orgName: classes.orgName,
    oppName: classes.oppName,
    verifyCheck: classes.verifyCheck
  };

  const addressPanelClassNames = {
    address: classes.addressLine,
    openNow: classes.openNow
  };

  return (
    <Card className={classes.resourceCard}>
      <Card.Body>
        <Container fluid>
          {resource.resource_type == 'Opportunity' && resourceHelpers.isCurrentlyUnavailable(resource) && (
            <ResourceCardBanner>{t('Currently unavailable')}</ResourceCardBanner>
          )}
          <Row className="align-items-start position-relative">
            <Col xs={12} xxl={7}>
              <OrgNameAndTitlePanel
                resource={resource}
                additionalClass={orgNameAndTitlePanelClassNames}
                linkTitle={true}
                linkSubtitle={true}
                displayVerification={true}
              />
            </Col>

            <Col xs={12} xxl={5} className="d-xxl-flex justify-content-xxl-end mt-2 mt-xxl-0 mb-3 mb-xxl-0">
              <ActionButtons buttons={actionButtons} additionalClasses={classes.actionBtn} resource={resource} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={8} lg={8}>
              <AddressPanel showOpenNow={true} resource={resource} additionalClass={addressPanelClassNames} />
              <div className="mt-2">
                <HasEligibilityRequirements resource={resource} />
              </div>
              <div className="mt-2">
                <VirtualAvailableBadge resource={resource} />
              </div>
              {resourceNetworkIds.length > 0 && (
                <div className="mt-2 d-flex gap-1">
                  {resourceNetworkIds.map((id) => (
                    <CLRIcon key={id} orgNetwork={orgNetwork} orgNetworkId={id} />
                  ))}
                </div>
              )}
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12} md={8} lg={8}>
              <div className="d-flex align-items-center gap-4">
                <StarRating stars={resource.rating} />
                <ServiceUtilization count={totalUtilizations?.[resource.id]} />
              </div>
            </Col>
          </Row>
          <Row className="mt-2">
            <p>{resource.description}</p>
          </Row>
          {showSuggestEdits && (
            <Row>
              <SuggestEditPanel resource={resource} additionalClass={suggestEditPanelClassName} project="locationB" />
            </Row>
          )}
          <Row className="d-flex">
            {/* For non-desktop resolution */}
            <div className={classes.cardDivider}></div>
            <Col xs={12} md={6} className={classes.cardButtons}>
              <ButtonPanel panelButtons={panelButtons} openPanel={openPanel} additionalClasses={classes.cardButton} disabled={false} />
            </Col>
            {showResourceRemoveFromCollectionLink && (
              <Col xs={12} md={6} className={classes.cardRemoveButtons}>
                <Button
                  type="link"
                  text={t('Remove from collection')}
                  onButtonClick={removeItemFromCollection}
                  styles={{ color: 'red' }}
                  additionalClasses={classes.removeResourceButton}
                />
              </Col>
            )}
          </Row>
          <Row>
            {
              {
                info: <InfoPanel resource={resource} includeFilterNote={includeFilterNote} language={language} />,
                contact: <ContactPanel resource={resource} language={language} />,
                map: <MapPanel resource={resource} language={language} />
              }[openPanel]
            }
            {openPanel && (
              <>
                <Col xs={12} className={classes.cardDivider}></Col>
                <Col xs={12} className={classes.closePanelSection}>
                  <Button type="link" text={t('Close details')} onButtonClick={closePanel} additionalClasses={classes.closePanelButton} />
                </Col>
              </>
            )}
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
};

ResourceCard.propTypes = {
  resource: OrganizationPropType,
  actionButtons: PropTypes.arrayOf(PropTypes.string),
  language: PropTypes.string,
  includeFilterNote: PropTypes.bool,
  totalUtilizations: PropTypes.object,
  showSuggestEdits: PropTypes.bool,
  showResourceRemoveFromCollectionLink: PropTypes.bool,
  removeItemFromCollection: PropTypes.func
};

export default ResourceCard;
